import { useState, useEffect } from "react";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { farmService } from "@/services/farmService";
import { alertsService } from "@/services/alertsService";
import { handleApiError } from "@/services/api";
import { useAuthStore } from "@/store/useAuthStore";
import type { AlertsDigestConfig, Farm, FarmFeatures, FarmInvoiceSettings, FarmTargets } from "@/types/models";

const textareaClass =
  "w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

const selectClass =
  "w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// Parse an optional performance-target input. Empty = unset (undefined); a
// non-empty value must be a number > 0 (and ≤ max when given), else throws so the
// form surfaces the error instead of silently storing a bad goal.
function parseTarget(raw: string, label: string, max?: number): number | undefined {
  const t = raw.trim();
  if (t === "") return undefined;
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0 || (max !== undefined && n > max)) {
    throw new Error(`${label} must be a number greater than 0${max !== undefined ? ` and at most ${max}` : ""}.`);
  }
  return n;
}

export default function FarmSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Basic details
  const [name, setName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [location, setLocation] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [gstNumber, setGstNumber] = useState("");

  // Tax & registration
  const [panNumber, setPanNumber] = useState("");
  const [msmeNumber, setMsmeNumber] = useState("");
  const [email, setEmail] = useState("");
  const [stateName, setStateName] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [productionAddress, setProductionAddress] = useState("");

  // Invoice settings (JSONB)
  const [bankName, setBankName] = useState("");
  const [bankAccountName, setBankAccountName] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [bankBranchIfsc, setBankBranchIfsc] = useState("");
  const [declarationText, setDeclarationText] = useState("");
  const [jurisdictionText, setJurisdictionText] = useState("");

  // Feature toggles
  const [dailyReportMode, setDailyReportMode] = useState(false);
  const [creditControl, setCreditControl] = useState<"off" | "warn" | "block">("off");
  const [cashOpeningFloat, setCashOpeningFloat] = useState("");
  const [cashOpeningDate, setCashOpeningDate] = useState("");

  // Performance targets (JSONB under features.targets) — kept as strings so an
  // empty field means "unset", not 0.
  const [targetHdp, setTargetHdp] = useState("");
  const [targetCostPerEgg, setTargetCostPerEgg] = useState("");
  const [targetFcr, setTargetFcr] = useState("");
  const [targetMortality, setTargetMortality] = useState("");
  // Age (weeks) at which a layer is treated as spent on the dashboard. Blank =
  // use the app default (72 wks); stored under features.spent_layer_age_weeks.
  const [spentLayerAge, setSpentLayerAge] = useState("");
  // Deaths alert limit for birds 8 weeks and older (% of the flock per day).
  // Blank = the app default (0.05%); stored under features.mortality_alert_pct.
  const [mortalityAlertPct, setMortalityAlertPct] = useState("");

  // Email digest (features.alerts_digest). Off by default; cadence persists even
  // when disabled so toggling back on keeps the owner's choices.
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [digestFrequency, setDigestFrequency] = useState<"weekly" | "daily">("weekly");
  const [digestDay, setDigestDay] = useState(1); // Monday
  const [digestHour, setDigestHour] = useState(7);
  const [digestMinSeverity, setDigestMinSeverity] = useState<"warning" | "critical">("warning");
  const [testSending, setTestSending] = useState(false);
  const [testMsg, setTestMsg] = useState("");

  // The full loaded features object. Saving replaces the whole JSONB column, so
  // we spread this to preserve keys this form doesn't edit (e.g. market_egg_price,
  // set out-of-band and read by the backend P&L) instead of wiping them.
  const [loadedFeatures, setLoadedFeatures] = useState<FarmFeatures>({});

  const setFarm = useAuthStore((s) => s.setFarm);

  useEffect(() => {
    // Only the latest load fills the form. In development React runs this
    // effect twice, and a second load landing after the owner started typing
    // put the stored values back over what they typed — the e2e run saw a
    // location typed, saved, and stored blank.
    let superseded = false;
    farmService
      .get()
      .then((farm: Farm) => {
        if (superseded) return;
        setName(farm.name);
        setOwnerName(farm.owner_name || "");
        setLocation(farm.location || "");
        setPhone(farm.phone || "");
        setAddress(farm.address || "");
        setGstNumber(farm.gst_number || "");
        setPanNumber(farm.pan_number || "");
        setMsmeNumber(farm.msme_number || "");
        setEmail(farm.email || "");
        setStateName(farm.state_name || "");
        setStateCode(farm.state_code || "");
        setProductionAddress(farm.production_address || "");
        const inv = farm.invoice_settings;
        if (inv) {
          setBankName(inv.bank_name || "");
          setBankAccountName(inv.bank_account_name || "");
          setBankAccountNumber(inv.bank_account_number || "");
          setBankBranchIfsc(inv.bank_branch_ifsc || "");
          setDeclarationText(inv.declaration_text || "");
          setJurisdictionText(inv.jurisdiction_text || "");
        }
        setLoadedFeatures(farm.features ?? {});
        setDailyReportMode(!!farm.features?.daily_report_mode);
        setCreditControl(farm.features?.credit_control ?? "off");
        setCashOpeningFloat(farm.features?.cash_opening_float != null ? String(farm.features.cash_opening_float) : "");
        setCashOpeningDate(farm.features?.cash_opening_date ?? "");
        const t = farm.features?.targets;
        setTargetHdp(t?.hdp_pct != null ? String(t.hdp_pct) : "");
        setTargetCostPerEgg(t?.cost_per_egg != null ? String(t.cost_per_egg) : "");
        setTargetFcr(t?.fcr != null ? String(t.fcr) : "");
        setTargetMortality(t?.mortality_pct != null ? String(t.mortality_pct) : "");
        setSpentLayerAge(
          farm.features?.spent_layer_age_weeks != null
            ? String(farm.features.spent_layer_age_weeks)
            : "",
        );
        setMortalityAlertPct(
          farm.features?.mortality_alert_pct != null ? String(farm.features.mortality_alert_pct) : "",
        );
        const d = farm.features?.alerts_digest;
        if (d) {
          setDigestEnabled(!!d.enabled);
          if (d.frequency === "daily" || d.frequency === "weekly") setDigestFrequency(d.frequency);
          if (d.day_of_week != null) setDigestDay(d.day_of_week);
          if (d.hour != null) setDigestHour(d.hour);
          if (d.min_severity === "critical" || d.min_severity === "warning") setDigestMinSeverity(d.min_severity);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!superseded) setLoading(false);
      });
    return () => {
      superseded = true;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate targets before touching the network so a bad goal is caught here.
    // Merge onto the loaded targets so keys this form doesn't own (e.g. a future
    // fcr_pct set out-of-band) survive; set/clear only the two fields it controls.
    const targets: FarmTargets = { ...(loadedFeatures.targets ?? {}) };
    let spentAge: number | undefined;
    let deathsAlert: number | undefined;
    try {
      const hdp = parseTarget(targetHdp, "Target HDP%", 100);
      const cpe = parseTarget(targetCostPerEgg, "Target cost/egg", 100);
      const fcr = parseTarget(targetFcr, "Target FCR", 20);
      const mort = parseTarget(targetMortality, "Target mortality%", 100);
      if (hdp !== undefined) targets.hdp_pct = hdp;
      else delete targets.hdp_pct;
      if (cpe !== undefined) targets.cost_per_egg = cpe;
      else delete targets.cost_per_egg;
      if (fcr !== undefined) targets.fcr = fcr;
      else delete targets.fcr;
      if (mort !== undefined) targets.mortality_pct = mort;
      else delete targets.mortality_pct;
      // A layer reaches point-of-lay ~18 wks, so a spent age below that is
      // nonsensical; cap at a generous 250 wks.
      spentAge = parseTarget(spentLayerAge, "Spent-layer age (weeks)", 250);
      if (spentAge !== undefined && spentAge < 18) {
        throw new Error("Spent-layer age (weeks) must be at least 18.");
      }
      // Same bounds as the server (parseMortalityAlertPct): above 5% a day the
      // "alert" warns of nothing; 0 would make every death critical.
      deathsAlert = parseTarget(mortalityAlertPct, "Deaths alert (% a day)", 5);
    } catch (err) {
      setError((err as Error).message);
      setSuccess("");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const invoiceSettings: FarmInvoiceSettings = {};
      if (bankName) invoiceSettings.bank_name = bankName;
      if (bankAccountName) invoiceSettings.bank_account_name = bankAccountName;
      if (bankAccountNumber) invoiceSettings.bank_account_number = bankAccountNumber;
      if (bankBranchIfsc) invoiceSettings.bank_branch_ifsc = bankBranchIfsc;
      if (declarationText) invoiceSettings.declaration_text = declarationText;
      if (jurisdictionText) invoiceSettings.jurisdiction_text = jurisdictionText;

      // The PUT replaces the whole features column, so spread the loaded object
      // first to keep keys this form doesn't own (market_egg_price, future flags),
      // then override the toggle. Only write `targets` when it has something —
      // an empty object is left absent so "no goals set" stays key-absent.
      const features: FarmFeatures = { ...loadedFeatures, daily_report_mode: dailyReportMode };
      // Retired switches (2026-10), dropped rather than carried: "Apply credit
      // to bills" is always available where credit sits beside open bills, and
      // the bank "Link" (choose which bills a row pays) went because bills
      // settle oldest-first — a hand-picked allocation would be re-settled away.
      delete (features as Record<string, unknown>).bank_bulk_apply;
      delete (features as Record<string, unknown>).settlement_modal;
      if (Object.keys(targets).length > 0) features.targets = targets;
      else delete features.targets;
      // "off" is the absence of the key, not a stored value — same convention as
      // targets above, and it keeps a farm that never touched this out of the
      // JSONB entirely.
      if (creditControl === "off") delete features.credit_control;
      else features.credit_control = creditControl;
      // Cash-in-hand opening float: only persist when a valid amount is entered.
      const cof = parseFloat(cashOpeningFloat);
      if (cashOpeningFloat.trim() !== "" && !Number.isNaN(cof) && cof >= 0) {
        features.cash_opening_float = cof;
        if (cashOpeningDate) features.cash_opening_date = cashOpeningDate;
        else delete features.cash_opening_date;
      } else {
        delete features.cash_opening_float;
        delete features.cash_opening_date;
      }
      if (spentAge !== undefined) features.spent_layer_age_weeks = spentAge;
      else delete features.spent_layer_age_weeks;
      if (deathsAlert !== undefined) features.mortality_alert_pct = deathsAlert;
      else delete features.mortality_alert_pct;

      // Persist the digest config only once it's been used — enabled now, or
      // configured before (so toggling off keeps the cadence). A farm that never
      // touches it keeps the key absent, like targets/spent_layer_age.
      if (digestEnabled || loadedFeatures.alerts_digest) {
        const digest: AlertsDigestConfig = {
          enabled: digestEnabled,
          frequency: digestFrequency,
          day_of_week: digestDay,
          hour: digestHour,
          min_severity: digestMinSeverity,
        };
        features.alerts_digest = digest;
      } else {
        delete features.alerts_digest;
      }

      const updated = await farmService.update({
        name,
        owner_name: ownerName,
        location,
        phone,
        address,
        gst_number: gstNumber,
        pan_number: panNumber,
        msme_number: msmeNumber,
        email,
        state_name: stateName,
        state_code: stateCode,
        production_address: productionAddress,
        invoice_settings: invoiceSettings,
        features,
      });
      setFarm(updated);
      setSuccess("Farm details saved successfully.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestDigest = async () => {
    setTestSending(true);
    setTestMsg("");
    try {
      await alertsService.sendTestDigest();
      setTestMsg("Test digest sent — check your inbox.");
    } catch (err) {
      setTestMsg(handleApiError(err));
    } finally {
      setTestSending(false);
    }
  };

  const sectionHeading = "mt-6 mb-3 text-base font-semibold text-gray-700 dark:text-white/80 border-b border-gray-200 dark:border-gray-700 pb-2";

  return (
    <>
      <PageMeta title="Farm Settings | Poultry Book" description="Manage farm details" />
      <PageBreadcrumb pageTitle="Farm Settings" />

      <div className="mx-auto max-w-2xl card p-5 sm:p-8">
        <h3 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          Farm Details
        </h3>

        {error && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 rounded-lg bg-success-50 p-3 text-sm text-success-600 dark:bg-success-500/15 dark:text-success-500">
            {success}
          </div>
        )}

        {loading ? (
          <div className="py-10 text-center text-gray-500 dark:text-gray-400">Loading...</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Details */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="farmName">Farm Name *</Label>
                <Input id="farmName" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Green Valley Farms" required />
              </div>
              <div>
                <Label htmlFor="farmOwner">Owner Name</Label>
                <Input id="farmOwner" type="text" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="e.g. John Doe" />
              </div>
              <div>
                <Label htmlFor="farmPhone">Phone</Label>
                <Input id="farmPhone" type="text" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. 9876543210" />
              </div>
              <div>
                <Label htmlFor="farmEmail">Email</Label>
                <Input id="farmEmail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="e.g. farm@gmail.com" />
              </div>
              <div>
                <Label htmlFor="farmLocation">Location</Label>
                <Input id="farmLocation" type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Maharashtra" />
              </div>
            </div>
            <div>
              <Label htmlFor="farmAddress">Registered Address</Label>
              <textarea id="farmAddress" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Full registered address for invoices" rows={2} className={textareaClass} />
            </div>
            <div>
              <Label htmlFor="productionAddress">Production Unit Address</Label>
              <textarea id="productionAddress" value={productionAddress} onChange={(e) => setProductionAddress(e.target.value)} placeholder="Production unit / farm address" rows={2} className={textareaClass} />
            </div>

            {/* Tax & Registration */}
            <h4 className={sectionHeading}>Tax & Registration</h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="farmGst">GST Number</Label>
                <Input id="farmGst" type="text" value={gstNumber} onChange={(e) => setGstNumber(e.target.value)} placeholder="e.g. 27AAAAA0000A1Z5" />
              </div>
              <div>
                <Label htmlFor="panNumber">PAN</Label>
                <Input id="panNumber" type="text" value={panNumber} onChange={(e) => setPanNumber(e.target.value)} placeholder="e.g. AAAAA0000A" />
              </div>
              <div>
                <Label htmlFor="msmeNumber">MSME / Udyam Number</Label>
                <Input id="msmeNumber" type="text" value={msmeNumber} onChange={(e) => setMsmeNumber(e.target.value)} placeholder="e.g. UDYAM-XX-00-0000000" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="stateName">State</Label>
                  <Input id="stateName" type="text" value={stateName} onChange={(e) => setStateName(e.target.value)} placeholder="e.g. Maharashtra" />
                </div>
                <div>
                  <Label htmlFor="stateCode">Code</Label>
                  <Input id="stateCode" type="text" value={stateCode} onChange={(e) => setStateCode(e.target.value)} placeholder="e.g. 27" />
                </div>
              </div>
            </div>

            {/* Bank Details for Invoice */}
            <h4 className={sectionHeading}>Bank Details (for Invoice)</h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="bankAccountName">A/c Holder Name</Label>
                <Input id="bankAccountName" type="text" value={bankAccountName} onChange={(e) => setBankAccountName(e.target.value)} placeholder="e.g. GREEN VALLEY FARMS" />
              </div>
              <div>
                <Label htmlFor="bankName">Bank Name</Label>
                <Input id="bankName" type="text" value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. State Bank of India" />
              </div>
              <div>
                <Label htmlFor="bankAccountNumber">Account Number</Label>
                <Input id="bankAccountNumber" type="text" value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value)} placeholder="e.g. 12345678901234" />
              </div>
              <div>
                <Label htmlFor="bankBranchIfsc">Branch & IFSC</Label>
                <Input id="bankBranchIfsc" type="text" value={bankBranchIfsc} onChange={(e) => setBankBranchIfsc(e.target.value)} placeholder="e.g. Main Branch, Mumbai & SBIN0000001" />
              </div>
            </div>

            {/* Invoice Footer */}
            <h4 className={sectionHeading}>Invoice Footer</h4>
            <div>
              <Label htmlFor="declarationText">Declaration Text</Label>
              <textarea id="declarationText" value={declarationText} onChange={(e) => setDeclarationText(e.target.value)} placeholder="We declare that this invoice shows the actual price..." rows={2} className={textareaClass} />
            </div>
            <div>
              <Label htmlFor="jurisdictionText">Jurisdiction</Label>
              <Input id="jurisdictionText" type="text" value={jurisdictionText} onChange={(e) => setJurisdictionText(e.target.value)} placeholder="e.g. SUBJECT TO MUMBAI JURISDICTION" />
            </div>

            {/* Features */}
            <h4 className={sectionHeading}>Features</h4>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={dailyReportMode}
                onChange={(e) => setDailyReportMode(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-white/80">
                  Daily Report Mode
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  When enabled, supervisors and managers see a simplified single-form view for daily data entry instead of the full navigation.
                </p>
              </div>
            </label>


            <div className="border-t border-gray-100 pt-4 dark:border-gray-800">
              <label htmlFor="creditControl" className="text-sm font-medium text-gray-700 dark:text-white/80">
                Credit control on sales
              </label>
              <select
                id="creditControl"
                value={creditControl}
                onChange={(e) => setCreditControl(e.target.value as "off" | "warn" | "block")}
                className="mt-2 h-11 w-full max-w-sm rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
              >
                <option value="off">Off — never check</option>
                <option value="warn">Warn — show the problem, allow the sale</option>
                <option value="block">Block — refuse the sale, owner can approve</option>
              </select>
              <p className="mt-2 max-w-2xl text-xs text-gray-500 dark:text-gray-400">
                Checks a buyer against two things when a sale is recorded: how much they already owe
                against their credit limit (set per buyer under Master Data &rarr; Buyers; blank means
                no limit), and whether any of their invoices are past due. On <strong>warn</strong> the
                sale still goes through &mdash; use it first, while you make sure the receivables are
                accurate. On <strong>block</strong> a sale that takes credit is refused, and only an
                owner can approve it; every approval is recorded in the activity log.
              </p>
              <p className="mt-2 max-w-2xl text-xs text-gray-500 dark:text-gray-400">
                This is a prompt at the till, not a lock. Walk-in sales with no buyer are never
                affected, a sale paid in full at the counter is never refused (it adds nothing to what
                the buyer owes), and <strong>editing a sale afterwards is not checked</strong> &mdash;
                a recorded sale can still be raised past the limit.
              </p>
            </div>

            {/* Cash in hand (opening balance) */}
            <h4 className={sectionHeading}>Cash in hand</h4>
            <p className="-mt-1 mb-1 text-xs text-gray-500 dark:text-gray-400">
              Physical cash on hand at the <span className="font-medium">end</span> of a baseline date. The Finance
              Cockpit&rsquo;s &ldquo;Net cash in hand&rdquo; adds everything recorded through the cash account
              <span className="font-medium"> after</span> that date: cash sales, receipts, payments, expenses, wage
              advances and vouchers. Payroll runs are booked to the bank, so wages paid in cash are not taken off.
              Leave blank to hide the metric.
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="cashOpeningFloat">Opening cash (₹)</Label>
                <Input
                  id="cashOpeningFloat"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={cashOpeningFloat}
                  onChange={(e) => setCashOpeningFloat(e.target.value)}
                  placeholder="e.g. 50000"
                />
              </div>
              <div>
                <Label htmlFor="cashOpeningDate">As of date</Label>
                <Input
                  id="cashOpeningDate"
                  type="date"
                  value={cashOpeningDate}
                  onChange={(e) => setCashOpeningDate(e.target.value)}
                />
              </div>
            </div>

            {/* Performance Targets */}
            <h4 className={sectionHeading}>Performance Targets</h4>
            <p className="-mt-1 mb-1 text-xs text-gray-500 dark:text-gray-400">
              Goals to measure each flock against. When set, the flock card, comparison page, and farm P&amp;L
              show how far actuals are above or below target. Leave blank to disable.
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="targetHdp">Target HDP (%)</Label>
                <Input
                  id="targetHdp"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  inputMode="decimal"
                  value={targetHdp}
                  onChange={(e) => setTargetHdp(e.target.value)}
                  placeholder="e.g. 85"
                />
              </div>
              <div>
                <Label htmlFor="targetCostPerEgg">Target cost / egg (₹)</Label>
                <Input
                  id="targetCostPerEgg"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={targetCostPerEgg}
                  onChange={(e) => setTargetCostPerEgg(e.target.value)}
                  placeholder="e.g. 4.00"
                />
              </div>
              <div>
                <Label htmlFor="targetFcr">Target FCR</Label>
                <Input
                  id="targetFcr"
                  type="number"
                  min="0"
                  max="20"
                  step="0.01"
                  inputMode="decimal"
                  value={targetFcr}
                  onChange={(e) => setTargetFcr(e.target.value)}
                  placeholder="e.g. 2.20"
                />
              </div>
              <div>
                <Label htmlFor="targetMortality">Target mortality (%)</Label>
                <Input
                  id="targetMortality"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  inputMode="decimal"
                  value={targetMortality}
                  onChange={(e) => setTargetMortality(e.target.value)}
                  placeholder="e.g. 8"
                />
              </div>
            </div>

            {/* Flock Lifecycle */}
            <h4 className={sectionHeading}>Flock Lifecycle</h4>
            <p className="-mt-1 mb-1 text-xs text-gray-500 dark:text-gray-400">
              Age at which a layer flock is past productive life and shown as &ldquo;spent&rdquo; on the
              dashboard instead of &ldquo;laying&rdquo;. Leave blank to use the default (72 weeks).
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="spentLayerAge">Spent-layer age (weeks)</Label>
                <Input
                  id="spentLayerAge"
                  type="number"
                  min="18"
                  max="250"
                  step="1"
                  inputMode="numeric"
                  value={spentLayerAge}
                  onChange={(e) => setSpentLayerAge(e.target.value)}
                  placeholder="e.g. 72"
                />
              </div>
              <div>
                <Label htmlFor="mortalityAlertPct">Deaths alert (% of the flock a day)</Label>
                <Input
                  id="mortalityAlertPct"
                  type="number"
                  min="0"
                  max="5"
                  step="any"
                  inputMode="decimal"
                  value={mortalityAlertPct}
                  onChange={(e) => setMortalityAlertPct(e.target.value)}
                  placeholder="0.05"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  For birds 8 weeks and older: more deaths than this in one day raises a critical alert.
                  Blank uses 0.05% (5 birds in 10,000). Chicks keep their own limits.
                </p>
              </div>
            </div>

            {/* Email Digest */}
            <h4 className={sectionHeading}>Email Digest</h4>
            <p className="-mt-1 mb-1 text-xs text-gray-500 dark:text-gray-400">
              Email the farm&rsquo;s &ldquo;needs attention&rdquo; alerts to owners on a schedule, so you
              don&rsquo;t have to open the app to catch a spent flock, a mortality spike, or an overdue bill.
            </p>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={digestEnabled}
                onChange={(e) => setDigestEnabled(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-white/80">
                  Send email digests
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Sent to active owners with an email address. No email is sent when there&rsquo;s nothing to report.
                </p>
              </div>
            </label>

            {digestEnabled && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="digestFrequency">Frequency</Label>
                  <select
                    id="digestFrequency"
                    value={digestFrequency}
                    onChange={(e) => setDigestFrequency(e.target.value as "weekly" | "daily")}
                    className={selectClass}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="daily">Daily</option>
                  </select>
                </div>
                {digestFrequency === "weekly" && (
                  <div>
                    <Label htmlFor="digestDay">Day of week</Label>
                    <select
                      id="digestDay"
                      value={digestDay}
                      onChange={(e) => setDigestDay(Number(e.target.value))}
                      className={selectClass}
                    >
                      {DAY_NAMES.map((day, i) => (
                        <option key={i} value={i}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <Label htmlFor="digestHour">Send time (IST)</Label>
                  <select
                    id="digestHour"
                    value={digestHour}
                    onChange={(e) => setDigestHour(Number(e.target.value))}
                    className={selectClass}
                  >
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={h}>
                        {String(h).padStart(2, "0")}:00
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="digestMinSeverity">Include</Label>
                  <select
                    id="digestMinSeverity"
                    value={digestMinSeverity}
                    onChange={(e) => setDigestMinSeverity(e.target.value as "warning" | "critical")}
                    className={selectClass}
                  >
                    <option value="warning">All alerts</option>
                    <option value="critical">Critical only</option>
                  </select>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Button type="button" size="sm" variant="outline" onClick={handleSendTestDigest} disabled={testSending}>
                {testSending ? "Sending..." : "Send test digest to me"}
              </Button>
              {testMsg && <span className="text-xs text-gray-500 dark:text-gray-400">{testMsg}</span>}
            </div>

            <div className="flex justify-end pt-4">
              <Button type="submit" size="sm" disabled={saving || !name}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
